import React, { useState } from "react";

interface CommentComposerProps {
  label?: string;
  placeholder?: string;
  submitLabel?: string;
  disabled?: boolean;
  onSubmit: (content: string) => Promise<void>;
  onCancel?: () => void;
}

const MAX_COMMENT_LENGTH = 3000;

const CommentComposer: React.FC<CommentComposerProps> = ({
  label = "Reply to selected comment",
  placeholder = "Write a focused reply…",
  submitLabel = "Post reply",
  disabled = false,
  onSubmit,
  onCancel,
}) => {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = content.trim();
  const isTooLong = content.length > MAX_COMMENT_LENGTH;
  const canSubmit = trimmed.length > 0 && !isTooLong && !disabled && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      setError(null);
      await onSubmit(trimmed);
      setContent("");
    } catch (nextError: any) {
      setError(nextError?.message || "Failed to post reply. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="comment-explorer-composer">
      <div className="comment-explorer-composer-head">
        <h4>{label}</h4>
        <span className={isTooLong ? "is-over-limit" : ""}>
          {content.length}/{MAX_COMMENT_LENGTH}
        </span>
      </div>

      <textarea
        value={content}
        disabled={disabled || submitting}
        placeholder={placeholder}
        maxLength={MAX_COMMENT_LENGTH + 200}
        onChange={(event) => setContent(event.target.value)}
      />

      {error && <div className="comment-explorer-inline-error">{error}</div>}
      {isTooLong && (
        <div className="comment-explorer-inline-error">
          Comment is too long. Please keep it under {MAX_COMMENT_LENGTH} characters.
        </div>
      )}

      <div className="comment-explorer-composer-actions">
        {onCancel && (
          <button type="button" className="comment-explorer-secondary-button" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="comment-explorer-primary-button"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {submitting ? "Posting…" : submitLabel}
        </button>
      </div>
    </div>
  );
};

export default CommentComposer;
